import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import StatusPill from "@/components/StatusPill";
import { fmtDateTime, titleCase } from "@/lib/format";
import { Send, Mail, MessageSquare, Megaphone, Users, Filter, CheckCircle2, Eye } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
const TYPES = ["", "residential", "commercial", "rolloff", "property_mgmt", "municipal"];

export default function Communications() {
    const [tab, setTab] = useState("broadcast");
    const [logs, setLogs] = useState([]);
    const [customers, setCustomers] = useState([]);

    // Broadcast state
    const [templates, setTemplates] = useState([]);
    const [templateId, setTemplateId] = useState("");
    const [customSubject, setCustomSubject] = useState("");
    const [customBody, setCustomBody] = useState("");
    const [channel, setChannel] = useState("email");
    const [audience, setAudience] = useState({ type: "", route_day: "", has_balance: false });
    const [preview, setPreview] = useState(null);
    const [sending, setSending] = useState(false);

    // 1:1 compose state
    const [oneOne, setOneOne] = useState({ channel: "email", to: "", subject: "", body: "", customer_id: "" });

    const load = () => api.get("/communications").then(({ data }) => setLogs(data));

    useEffect(() => {
        load();
        api.get("/customers").then(({ data }) => setCustomers(data));
        api.get("/communications/templates").then(({ data }) => setTemplates(data));
    }, []);

    const tmpl = templates.find((t) => t.id === templateId);
    const subjectPreview = customSubject || tmpl?.subject || "";
    const bodyPreview = customBody || tmpl?.body || "";

    const previewAudience = async () => {
        const { data } = await api.post("/communications/audience-preview", audience);
        setPreview(data);
    };

    useEffect(() => { previewAudience(); /* eslint-disable-next-line */ }, [audience]);

    const sendBroadcast = async () => {
        if (!templateId && !customBody) return toast.error("Pick a template or write a custom message");
        setSending(true);
        try {
            const { data } = await api.post("/communications/broadcast", {
                template_id: templateId || null,
                channel, audience,
                custom_subject: customSubject || null,
                custom_body: customBody || null,
            });
            toast.success(`Broadcast sent — ${data.sent.email} email, ${data.sent.sms} SMS to ${data.audience_size} customers`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        } finally { setSending(false); }
    };

    const send1on1 = async () => {
        if (!oneOne.to || !oneOne.body) return toast.error("Recipient and message required");
        try {
            await api.post("/communications/send", oneOne);
            toast.success("Message sent");
            setOneOne({ channel: oneOne.channel, to: "", subject: "", body: "", customer_id: "" });
            load();
        } catch { toast.error("Failed"); }
    };

    return (
        <div data-testid="communications-page">
            <PageHeader title="Communications" subtitle="Broadcast templates, 1-on-1 messages and the full email + SMS log." />
            <div className="px-8 py-6">
                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList>
                        <TabsTrigger value="broadcast" data-testid="tab-broadcast"><Megaphone className="w-4 h-4 mr-1" />Broadcast</TabsTrigger>
                        <TabsTrigger value="compose" data-testid="tab-compose"><Send className="w-4 h-4 mr-1" />1-on-1 message</TabsTrigger>
                        <TabsTrigger value="log" data-testid="tab-log"><Mail className="w-4 h-4 mr-1" />Log</TabsTrigger>
                    </TabsList>

                    <TabsContent value="broadcast" className="mt-4 grid lg:grid-cols-3 gap-4">
                        <Card className="lg:col-span-1">
                            <CardContent className="p-6 space-y-4">
                                <div>
                                    <div className="overline mb-1">Template</div>
                                    <h3 className="font-heading text-lg font-semibold">Pick a message</h3>
                                </div>
                                <div className="space-y-2">
                                    <button
                                        onClick={() => { setTemplateId(""); setCustomSubject(""); setCustomBody(""); }}
                                        data-testid="tmpl-custom"
                                        className={`w-full text-left p-3 border rounded-md transition-colors ${
                                            !templateId ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/50"
                                        }`}>
                                        <div className="text-sm font-medium">Custom message</div>
                                        <div className="text-xs text-muted-foreground">Write your own subject + body</div>
                                    </button>
                                    {templates.map((t) => (
                                        <button key={t.id}
                                                onClick={() => { setTemplateId(t.id); setCustomSubject(""); setCustomBody(""); }}
                                                data-testid={`tmpl-${t.id}`}
                                                className={`w-full text-left p-3 border rounded-md transition-colors ${
                                                    templateId === t.id ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/50"
                                                }`}>
                                            <div className="text-sm font-medium">{titleCase(t.id)}</div>
                                            <div className="text-xs text-muted-foreground line-clamp-1">{t.subject}</div>
                                        </button>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="lg:col-span-2">
                            <CardContent className="p-6 space-y-4">
                                <div>
                                    <div className="overline mb-1">Compose + audience</div>
                                    <h3 className="font-heading text-lg font-semibold">Configure your broadcast</h3>
                                </div>

                                <div className="grid grid-cols-3 gap-3">
                                    <div className="space-y-1.5">
                                        <Label className="text-xs">Channel</Label>
                                        <Select value={channel} onValueChange={setChannel}>
                                            <SelectTrigger data-testid="bc-channel"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="email">Email only</SelectItem>
                                                <SelectItem value="sms">SMS only</SelectItem>
                                                <SelectItem value="both">Email + SMS</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label className="text-xs">Customer type</Label>
                                        <Select value={audience.type || "all"} onValueChange={(v) => setAudience({ ...audience, type: v === "all" ? "" : v })}>
                                            <SelectTrigger data-testid="bc-type"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">All types</SelectItem>
                                                {TYPES.filter(Boolean).map((t) => <SelectItem key={t} value={t}>{titleCase(t)}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label className="text-xs">Route day</Label>
                                        <Select value={audience.route_day || "all"} onValueChange={(v) => setAudience({ ...audience, route_day: v === "all" ? "" : v })}>
                                            <SelectTrigger data-testid="bc-day"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">All days</SelectItem>
                                                {DAYS.filter(Boolean).map((d) => <SelectItem key={d} value={d}>{titleCase(d)}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <label className="flex items-center gap-2 text-xs cursor-pointer">
                                    <input type="checkbox" checked={audience.has_balance}
                                           onChange={(e) => setAudience({ ...audience, has_balance: e.target.checked })}
                                           data-testid="bc-has-balance" />
                                    Only customers with an open balance
                                </label>

                                <div className="space-y-1.5">
                                    <Label className="text-xs">Subject {tmpl ? "(template)" : ""}</Label>
                                    <Input value={subjectPreview}
                                           onChange={(e) => setCustomSubject(e.target.value)}
                                           data-testid="bc-subject" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs">Message body</Label>
                                    <Textarea rows={5} value={bodyPreview}
                                              onChange={(e) => setCustomBody(e.target.value)}
                                              data-testid="bc-body" />
                                    <div className="text-[11px] text-muted-foreground">Use <code className="font-mono bg-secondary px-1 rounded">{`{{customer_name}}`}</code> to personalize per recipient.</div>
                                </div>

                                <div className="flex items-center gap-3 p-3 rounded-md border border-border bg-secondary/40" data-testid="bc-audience-preview">
                                    <Users className="w-4 h-4 text-primary shrink-0" />
                                    <div className="flex-1">
                                        <div className="text-sm font-medium">{preview?.count ?? "—"} customers will receive this</div>
                                        <div className="text-xs text-muted-foreground truncate">
                                            {preview?.sample?.slice(0, 3).map((c) => c.name).join(", ")}
                                            {preview?.sample && preview.sample.length > 3 ? ` +${preview.count - 3} more` : ""}
                                        </div>
                                    </div>
                                </div>

                                <Button onClick={sendBroadcast} className="w-full" disabled={sending || !preview?.count}
                                        data-testid="bc-send-btn">
                                    <Send className="w-4 h-4 mr-1" />
                                    {sending ? "Sending..." : `Send to ${preview?.count || 0} customers`}
                                </Button>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="compose" className="mt-4">
                        <OneOnOneCompose form={oneOne} setForm={setOneOne} customers={customers} send={send1on1} />
                    </TabsContent>

                    <TabsContent value="log" className="mt-4">
                        <Card>
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="font-heading text-lg font-semibold">Recent messages</h3>
                                    <div className="text-xs text-muted-foreground font-mono">{logs.length} total</div>
                                </div>
                                <div className="divide-y divide-border max-h-[70vh] overflow-y-auto" data-testid="comm-log">
                                    {logs.map((c) => (
                                        <div key={c.id} className="py-3 flex items-start gap-3">
                                            <div className="w-8 h-8 rounded-md border border-border surface flex items-center justify-center shrink-0">
                                                {c.channel === "email" ? <Mail className="w-4 h-4" /> : c.channel === "sms" ? <MessageSquare className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <div className="text-sm font-medium truncate">{c.subject || c.body.slice(0, 80)}</div>
                                                    <StatusPill status={c.status} />
                                                </div>
                                                <div className="text-xs text-muted-foreground mt-1">{c.channel.toUpperCase()} · {fmtDateTime(c.created_at)}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

function OneOnOneCompose({ form, setForm, customers, send }) {
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    return (
        <Card className="max-w-2xl">
            <CardContent className="p-6 space-y-3">
                <h3 className="font-heading text-lg font-semibold">Send a single message</h3>
                <div className="space-y-1.5">
                    <Label className="text-xs">Channel</Label>
                    <Select value={form.channel} onValueChange={(v) => set("channel", v)}>
                        <SelectTrigger data-testid="comm-channel"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="sms">SMS</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-1.5">
                    <Label className="text-xs">Customer</Label>
                    <Select value={form.customer_id || "none"} onValueChange={(v) => {
                        if (v === "none") { set("customer_id", ""); set("to", ""); return; }
                        const c = customers.find((x) => x.id === v);
                        set("customer_id", v);
                        set("to", form.channel === "email" ? c?.email || "" : c?.phone || "");
                    }}>
                        <SelectTrigger data-testid="comm-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="none">— None —</SelectItem>
                            {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">To</Label>
                    <Input value={form.to} onChange={(e) => set("to", e.target.value)} data-testid="comm-to" /></div>
                {form.channel === "email" && (
                    <div className="space-y-1.5"><Label className="text-xs">Subject</Label>
                        <Input value={form.subject} onChange={(e) => set("subject", e.target.value)} data-testid="comm-subject" /></div>
                )}
                <div className="space-y-1.5"><Label className="text-xs">Message</Label>
                    <Textarea rows={5} value={form.body} onChange={(e) => set("body", e.target.value)} data-testid="comm-body" /></div>
                <Button onClick={send} className="w-full" data-testid="comm-send-btn">
                    <Send className="w-4 h-4 mr-2" />Send
                </Button>
            </CardContent>
        </Card>
    );
}
